import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import { useNavigate, useParams } from 'react-router/dist'
import { FormProvider, useForm } from 'react-hook-form'
import axios, { CancelTokenSource } from 'axios'
import { El } from '~/components/Layout/style'
import notify from '~/components/Notify'
import { RouteTypes } from '~/routes/constants'
import { Progress } from 'semantic-ui-react'
import FormPart from './FormPart'
import { ClimaProgramService } from '~/api/services/climaProgram'
import { ClimaRegistrationData, createRegistrationFormData } from './utils'
import ClimaProgramQuery from '~/QueryHooks/ClimaProgramQuery'
import { handleErrors } from '~/utils/helpers'
import WelcomePart from './WelcomePart'
import { useHash } from '~/components/useHash'

const EditRegistration: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id, idFarmer } = useParams()
  const [hash] = useHash()
  const farmerName = hash.startsWith('#farmerName=') ? decodeURIComponent(hash.replace('#farmerName=', '')) : ''

  const [isBusy, setIsBusy] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [termsSubmited, setTermsSubmited] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const cancelTokenRef = useRef<CancelTokenSource | null>(null)

  const methods = useForm({
    mode: 'onSubmit'
  })

  const {
    data: registrationData,
    isLoading: isLoadingRegData,
    isFetching: isFetchinRegData
  } = ClimaProgramQuery.getClimaRegOptionsData()

  useEffect(() => {
    if (id) {
      fetchRegistration()
    }
  }, [id])

  async function fetchRegistration() {
    setIsBusy(true)
    try {
      const response = await ClimaProgramService.editRegistration(id)
      const responseFile = await ClimaProgramService.getRegistrationFile(id)
      const feedingPlanFile = {
        data: '',
        name: ''
      }
      if (responseFile?.data) {
        feedingPlanFile.data = responseFile.data
        feedingPlanFile.name = response.data.feedingPlanOriginalFilename
      }
      methods.reset({
        ...response.data,
        feedingPlanFile
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function onSubmit(data: ClimaRegistrationData) {
    data.farmerId = idFarmer
    const formData = createRegistrationFormData(data, !id)
    const source = axios.CancelToken.source()
    cancelTokenRef.current = source
    setIsUploading(true)
    setUploadProgress(0)

    const onUploadProgress = (progressEvent) => {
      const { loaded, total } = progressEvent
      if (total) {
        setUploadProgress(Math.round((loaded / total) * 100))
      }
    }

    try {
      if (id) {
        await ClimaProgramService.updateRegistration({ id, formData, onUploadProgress, cancelToken: source })
        notify(t('Registration updated successfully'), 'success')
      } else if (idFarmer) {
        await ClimaProgramService.registerClimaProgram({ formData, onUploadProgress, cancelToken: source })
        notify(t('Registration created successfully'), 'success')
      }
      navigate(RouteTypes.CLIMA_PROGRAM_REGISTRATIONS)
    } catch (err) {
      if (axios.isCancel(err)) {
        notify(t('Upload canceled'), 'error')
      } else {
        handleErrors(err)
      }
    }
    setIsUploading(false)
    setUploadProgress(0)
  }

  function renderFormPart() {
    return <FormProvider {...methods}>
      <FormPart
        milkOptions={registrationData?.milkAmounts || []}
        concentratedFeedsAmounts={registrationData?.concentratedFeedsAmounts || []}
      />
    </FormProvider>
  }

  function renderRegisterAction() {
    return <El.PageHeaderActionButton>
      {id ? t('Update') : t('Register')}
    </El.PageHeaderActionButton>
  }

  function renderWelcomePart() {
    const farmer = registrationData?.farmers?.find(farmer => farmer.id === id)
    return <WelcomePart
      farmer={farmer}
      handleClickTerms={(termsAccepted) => setTermsAccepted(termsAccepted)}
    />
  }

  function renderRegistration() {
    if (isLoadingRegData) {
      return
    }

    return <>
      {isUploading && (
        <Progress
          percent={uploadProgress}
          indicating
          progress
          style={{ marginBottom: '1rem' }}
        />
      )}
      {id || (termsAccepted && termsSubmited) ? renderFormPart() : renderWelcomePart()}
    </>
  }

  const renderAcceptTermsAction = () => (
    <El.PageHeaderActionButton
      positive
      size='large'
      type={'button'}
      onClick={() => setTermsSubmited(true)}
      disabled={!termsAccepted}
    >
      {t('Accept')}
    </El.PageHeaderActionButton>
  )

  return <Layout
    title={id ? t('Edit registration {{farmerName}}', { farmerName }) : t('Add registration {{farmerName}}', { farmerName })}
    segmented
    close={RouteTypes.CLIMA_PROGRAM_REGISTRATIONS}
    isBusy={isLoadingRegData || isFetchinRegData || isBusy}
    actions={id || termsSubmited ? renderRegisterAction() : renderAcceptTermsAction()}
    onSubmit={(id || termsSubmited) && methods.handleSubmit(onSubmit)}
  >
    {renderRegistration()}
  </Layout>
}

export default EditRegistration
